import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import {
  clearAnalyticsData,
  fetchDetailedAnalyticsAsync,
} from "../../../redux/features/analyticsSlice";
import moment from "moment";
import CustomTable from "../../../components/Tables/CustomTable";
import WhiteTitledCard from "../../../components/cards/WhiteTitledCard";
import OverviewCard from "../../../components/cards/OverviewCard";
import FilterByButton from "../../ui/FilterButton";
import SearchInput from "../../../components/form/SearchInput";
import MixedChart from "../../../components/charts/MixedChart";
import {
  revenueAnalyticsTableColumns,
  revenueAnalyticsTableRows,
} from "./tableUtils";
import {
  DollarSign,
  TrendingDown,
  TrendingUp,
  ShoppingCart,
} from "lucide-react";
import { formatNumberWithCommas } from "../../../helpers";

const yearFilterOptions = [
  { label: "This year", value: "this_year" },
  { label: "Last year", value: "last_year" },
  { label: "Last 6 months", value: "last_6_months" },
  { label: "Last 3 months", value: "last_3_months" },
];

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const DetailedAnalytics = () => {
  const [yearFilter, setYearFilter] = useState("this_year");
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const { loading, detailedAnalytics } = useAppSelector(
    (state) => state.analytics,
  );
  const dispatch = useAppDispatch();

  const getDateRange = (filter: string) => {
    switch (filter) {
      case "last_year":
        return {
          startDate: new Date(
            moment().subtract(1, "year").startOf("year").toISOString(),
          ),
          endDate: new Date(
            moment().subtract(1, "year").endOf("year").toISOString(),
          ),
        };
      case "last_6_months":
        return {
          startDate: new Date(
            moment().subtract(6, "months").startOf("month").toISOString(),
          ),
          endDate: new Date(moment().endOf("month").toISOString()),
        };
      case "last_3_months":
        return {
          startDate: new Date(
            moment().subtract(3, "months").startOf("month").toISOString(),
          ),
          endDate: new Date(moment().endOf("month").toISOString()),
        };
      default:
        return {
          startDate: new Date(moment().startOf("year").toISOString()),
          endDate: new Date(moment().endOf("year").toISOString()),
        };
    }
  };

  useEffect(() => {
    dispatch(clearAnalyticsData());
    const { startDate, endDate } = getDateRange(yearFilter);
    dispatch(
      fetchDetailedAnalyticsAsync({
        type: "revenue",
        page: page + 1,
        limit,
        startDate,
        endDate,
      }),
    );
  }, [yearFilter, page, limit, dispatch]);

  const revenueData = detailedAnalytics?.data ?? [];

  // Calculate summary totals from data
  const summary = {
    totalRevenue: revenueData.reduce(
      (sum: number, item: any) => sum + (item.totalRevenue ?? 0),
      0,
    ),
    totalWithdrawal: revenueData.reduce(
      (sum: number, item: any) => sum + (item.totalWithdrawalRevenue ?? 0),
      0,
    ),
    totalDeposit: revenueData.reduce(
      (sum: number, item: any) => sum + (item.totalDepositRevenue ?? 0),
      0,
    ),
    totalPurchase: revenueData.reduce(
      (sum: number, item: any) => sum + (item.totalPurchaseRevenue ?? 0),
      0,
    ),
  };

  // Aggregate data by month for charts
  const monthlyData = months.map((_, monthIndex) => {
    const monthItems = revenueData.filter((item: any) => {
      const itemDate = moment(item.summaryDate);
      return itemDate.month() === monthIndex;
    });
    return {
      totalRevenue: monthItems.reduce(
        (sum: number, item: any) => sum + (item.totalRevenue ?? 0),
        0,
      ),
      purchases: monthItems.reduce(
        (sum: number, item: any) => sum + (item.totalPurchaseRevenue ?? 0),
        0,
      ),
      deposits: monthItems.reduce(
        (sum: number, item: any) => sum + (item.totalDepositRevenue ?? 0),
        0,
      ),
      withdrawals: monthItems.reduce(
        (sum: number, item: any) => sum + (item.totalWithdrawalRevenue ?? 0),
        0,
      ),
    };
  });

  // Total revenue trend (bar chart)
  const totalRevenueSeries = [
    {
      name: "Total Revenue",
      type: "bar" as const,
      data: monthlyData.map((m) => m.totalRevenue),
      color: "#1E1B4B",
    },
  ];

  // Purchase revenue trend (line chart)
  const purchaseRevenueSeries = [
    {
      name: "Purchase Revenue",
      type: "line" as const,
      data: monthlyData.map((m) => m.purchases),
      color: "#4338CA",
    },
  ];

  // Deposits vs Withdrawal (two lines)
  const depositsWithdrawalSeries = [
    {
      name: "Withdrawal",
      type: "line" as const,
      data: monthlyData.map((m) => m.withdrawals),
      color: "#EF4444",
    },
    {
      name: "Deposit",
      type: "line" as const,
      data: monthlyData.map((m) => m.deposits),
      color: "#3B82F6",
    },
  ];

  const tableData = {
    columns: revenueAnalyticsTableColumns,
    rows: revenueAnalyticsTableRows(revenueData),
  };

  return (
    <div className="p-6 flex flex-col gap-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-2xl font-bold">Overview</p>
        <FilterByButton
          options={yearFilterOptions}
          onSelect={(value) => {
            setYearFilter(value);
            setPage(0);
          }}
          buttonLabel="This year"
          defaultValue={yearFilter}
        />
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <OverviewCard
          title="Total revenue"
          value={`₦${formatNumberWithCommas(summary.totalRevenue)}`}
          icon={TrendingUp}
          iconBg="bg-purple-600"
          isLoading={loading}
          loadingColor="#7C3AED"
          change="↑ 3.5%"
          isPositive={true}
          changeLabel="vs last year"
        />
        <OverviewCard
          title="Revenue withdrawal"
          value={`₦${formatNumberWithCommas(summary.totalWithdrawal)}`}
          icon={TrendingDown}
          iconBg="bg-purple-600"
          isLoading={loading}
          loadingColor="#7C3AED"
          change="↓ 1.5%"
          isPositive={false}
          changeLabel="vs last year"
        />
        <OverviewCard
          title="Revenue deposit"
          value={`₦${formatNumberWithCommas(summary.totalDeposit)}`}
          icon={DollarSign}
          iconBg="bg-purple-600"
          isLoading={loading}
          loadingColor="#7C3AED"
          change="↑ 3.5%"
          isPositive={true}
          changeLabel="vs last year"
        />
        <OverviewCard
          title="Revenue purchase"
          value={`₦${formatNumberWithCommas(summary.totalPurchase)}`}
          icon={ShoppingCart}
          iconBg="bg-purple-600"
          isLoading={loading}
          loadingColor="#7C3AED"
          change="↑ 3.5%"
          isPositive={true}
          changeLabel="vs last year"
        />
      </div>

      {/* Charts Row 1: Total revenue trend + Purchase revenue trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MixedChart
          title="Total revenue trend"
          xLabels={months}
          series={totalRevenueSeries}
        />
        <MixedChart
          title="Purchase revenue trend"
          xLabels={months}
          series={purchaseRevenueSeries}
        />
      </div>

      {/* Chart Row 2: Deposits VS Withdrawal */}
      <MixedChart
        title="Deposits VS Withdrawal"
        xLabels={months}
        series={depositsWithdrawalSeries}
      />

      {/* Supporting analytics table */}
      <WhiteTitledCard cardTitle="Supporting analytics table">
        <div className="p-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 mb-4">
            <FilterByButton
              options={yearFilterOptions}
              onSelect={() => {}}
              buttonLabel="Filter by"
            />
            <SearchInput
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <CustomTable
            data={tableData}
            noRecordText="Record is Empty"
            loading={loading}
            showFooter={true}
            totalPages={Math.ceil(
              Number(detailedAnalytics?.total ?? 0) /
                Number(detailedAnalytics?.limit ?? limit),
            )}
            itemsPerPage={limit}
            currentPage={page}
            onPageChange={(value: number) => setPage(value)}
            onLimitChange={(value: number) => {
              setLimit(value);
              setPage(0);
            }}
          />
        </div>
      </WhiteTitledCard>
    </div>
  );
};

export default DetailedAnalytics;
